// https://base-ui.com/react/components/dialog
import { Dialog } from "@base-ui/react/dialog";
import { useEffect, type ReactNode } from "react";
import cx from "ui/cx";
import IconClose from "ui/icons/IconClose";
import styles from "./Drawer.module.scss";

export interface DrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Fired when the drawer requests to close (close button, Esc, or backdrop click). */
  onOpenChange: (open: boolean) => void;
  /** Fired after the open or close animation finishes, with the state it settled into. */
  onOpenChangeComplete?: (open: boolean) => void;
  /**
   * Fired once the drawer has finished animating out. Clear the state behind `open` here.
   * Equivalent to the closed half of `onOpenChangeComplete`; prefer that for new call sites.
   */
  onExited?: () => void;
  /** Title shown in the drawer header, beside the close button. */
  title?: ReactNode;
  /** Optional label rendered above the title, for context such as the kind of record being edited. */
  subTitle?: ReactNode;
  /** Drawer body content. */
  children?: ReactNode;
  /** Optional slot for extra header actions, rendered between the title and close button. */
  titleActions?: ReactNode;
  /** Optional slot pinned to the bottom of the drawer, below the scrolling body. */
  footer?: ReactNode;
  /** One-off override for the footer. */
  footerClassName?: string;
  /** One-off override for the content panel. */
  className?: string;
}

/**
 * A panel that slides in from the right over a dimmed backdrop. Product-agnostic — pass `title` and `children`.
 */
export default function Drawer({
  open,
  onOpenChange,
  onOpenChangeComplete,
  onExited,
  title,
  subTitle,
  children,
  titleActions,
  footer,
  footerClassName,
  className,
}: DrawerProps) {
  // Lock body scroll while open; a non-modal dialog does not lock it for us.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      // Base UI reports the transition settling in both directions. No sentinel child is needed to
      // observe the exit: the drawer is still mounted when the animation ends, and unmounting would
      // be too late to tell a close apart from a route change.
      onOpenChangeComplete={(isOpen) => {
        onOpenChangeComplete?.(isOpen);
        if (!isOpen) {
          onExited?.();
        }
      }}
      // Base UI treats any non-false `modal` as modal for focus management, which aria-hides
      // everything outside the drawer. A Modal, Select or Popover layered over an open drawer
      // renders into the app tree outside it, so it disappears from assistive tech and from
      // role-based queries. Tab can walk out of the drawer as a result; see LAB-472.
      modal={false}
      // Close only via backdrop, Esc, or the close button — don't dismiss on interaction
      // with layers (modals, selects, popovers) portalled from within the drawer.
      disablePointerDismissal
    >
      <Dialog.Portal>
        {/* With pointer dismissal disabled, Base UI ignores backdrop presses; close explicitly. */}
        <Dialog.Backdrop className={styles.overlay} data-testid="drawer-overlay" onClick={() => onOpenChange(false)} />
        <Dialog.Popup className={cx(styles.content, className)} data-testid="drawer-content">
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
              <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            </div>
            {titleActions}
            <Dialog.Close className={styles.close} aria-label="Close" data-testid="drawer-close">
              <IconClose size={18} />
            </Dialog.Close>
          </div>
          <div className={styles.body}>{children}</div>
          {footer && (
            <div className={cx(styles.footer, footerClassName)} data-testid="drawer-footer">
              {footer}
            </div>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
